import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { api, formatApiError } from "../lib/api";
import { BookCover } from "../components/BookCover";
import { StarDisplay } from "../components/StarRating";
import { toast } from "sonner";
import { ArrowLeft, Compass, X } from "lucide-react";

export default function ListDetailPage() {
  const { listId } = useParams();
  const [data, setData] = useState(null); // null=loading, false=not found
  const [removing, setRemoving] = useState("");

  const load = async () => {
    try {
      const { data: payload } = await api.get(`/sections/${listId}/books`);
      setData(payload);
    } catch (e) {
      setData(false);
    }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [listId]);

  if (data === null) {
    return <div className="p-12 font-mono uppercase tracking-widest text-xs text-[#A3A3A3]">Loading list…</div>;
  }
  if (data === false) {
    return (
      <div className="p-12 max-w-3xl mx-auto">
        <div className="font-heading text-2xl text-[#525252]">List not found.</div>
        <Link to="/lists" className="font-mono uppercase tracking-widest text-xs text-[#0A0A0A] hover:underline mt-4 inline-block">Back to lists →</Link>
      </div>
    );
  }

  const removeBook = async (bookId) => {
    setRemoving(bookId);
    try {
      await api.delete(`/sections/${listId}/books/${bookId}`);
      toast.success("Removed from list");
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setRemoving("");
    }
  };

  const { section, books } = data;
  return (
    <div className="max-w-6xl mx-auto px-6 md:px-12 py-12 fade-up" data-testid="list-detail-page">
      <Link to="/lists" className="inline-flex items-center gap-1.5 font-mono uppercase tracking-widest text-[10px] text-[#525252] hover:text-[#0A0A0A] mb-8" data-testid="back-to-lists">
        <ArrowLeft className="w-3 h-3" strokeWidth={1.5} /> All lists
      </Link>

      <div className="flex items-end justify-between flex-wrap gap-6 mb-10">
        <div>
          <div className="font-mono uppercase tracking-[0.25em] text-xs text-[#4A4030]">List</div>
          <h1 className="font-heading text-4xl md:text-5xl text-[#0A0A0A] mt-3 tracking-tight" data-testid="list-detail-name">{section.name}</h1>
          <div className="font-mono uppercase tracking-widest text-[10px] text-[#525252] mt-2">
            {books.length} {books.length === 1 ? "book" : "books"}
          </div>
        </div>
        <Link
          to={`/recommendations?section=${section.id}`}
          className="inline-flex items-center gap-2 bg-[#0A0A0A] hover:bg-[#1F1F1F] text-[#F2EAD3] font-mono uppercase tracking-widest text-xs px-5 py-3 rounded-sm transition-colors"
          data-testid="list-picks-cta"
        >
          <Compass className="w-3.5 h-3.5" strokeWidth={1.5} /> Get picks from this list
        </Link>
      </div>

      {books.length === 0 ? (
        <div className="border border-dashed border-[rgba(0,0,0,0.12)] p-16 text-center">
          <div className="font-heading italic text-2xl text-[#525252]">No books in this list yet.</div>
          <p className="text-sm text-[#525252] mt-3">Open any book in your library and tap <em>Add to list</em> to drop it in here.</p>
          <Link to="/library" className="font-mono uppercase tracking-widest text-xs text-[#0A0A0A] hover:underline mt-4 inline-block">Open my library →</Link>
        </div>
      ) : (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-6 md:gap-8" data-testid="list-detail-books">
          {books.map((book) => (
            <div key={book.id} className="relative group" data-testid={`list-book-${book.id}`}>
              <button
                onClick={() => removeBook(book.id)}
                disabled={removing === book.id}
                className="absolute z-10 top-2 right-2 w-7 h-7 rounded-sm bg-[#F2EAD3]/90 text-[#7C1D1D] border border-[rgba(10,10,10,0.18)] flex items-center justify-center opacity-0 group-hover:opacity-100 hover:bg-[#7C1D1D]/10 transition-opacity"
                aria-label="Remove from list"
                data-testid={`remove-from-list-${book.id}`}
              >
                <X className="w-3.5 h-3.5" strokeWidth={1.5} />
              </button>
              <Link to={`/books/${book.id}`}>
                <BookCover src={book.cover_url} title={book.title} author={book.author} />
                <div className="mt-3 space-y-1">
                  <div className="font-heading text-base text-[#0A0A0A] leading-tight line-clamp-2">{book.title}</div>
                  <div className="font-mono text-[10px] uppercase tracking-widest text-[#525252]">{book.author}</div>
                  {book.avg_rating > 0 && (
                    <div className="flex items-center gap-2 pt-1">
                      <StarDisplay value={book.avg_rating} size={12} />
                    </div>
                  )}
                </div>
              </Link>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
